import { TAuthResetPasswordRequestPayload, authService } from "@/api/auth";
import { FormEvent, useCallback } from "react";
import { DefaultValues, useForm } from "react-hook-form";

import { resetPasswordRequestSchema } from "@/api/auth/auth.types";
import { useToast } from "@/components/ui";
import { handleFormError } from "@/utils/formErrorUtils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";

export type TUseResetPasswordFormProps = {
  defaultValues?: DefaultValues<TAuthResetPasswordRequestPayload>;
  token: string;
};

export const useResetPasswordForm = ({ defaultValues, token }: TUseResetPasswordFormProps) => {
  const form = useForm<TAuthResetPasswordRequestPayload>({
    resolver: zodResolver(resetPasswordRequestSchema),
    defaultValues: {
      ...defaultValues,
      token,
    },
  });

  const { toast } = useToast();
  const navigate = useNavigate();
  const handleSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      form
        .handleSubmit(async (payload) => {
          const response = await authService.resetPassword(payload);

          if (response.data) {
            toast({
              variant: "success",
              title: "Password reset successful!",
              description: "You can now log in with your new password.",
            });
            void navigate({ to: "/auth/login", search: { from: undefined } });
          }

          if (response.error) {
            // Use shared form error handling
            await handleFormError(response.error, form);
          }
        })(e)
        .catch(console.error);
    },
    [form, toast, navigate]
  );

  return {
    form,
    handleSubmit,
  };
};
